/**
 * InsiSearchModal (T06) — the reusable INSi identity-search modal.
 *
 * Used by both the create flow (T08) and the edit / Flow B re-search (T09).
 * Behaviour is fixed by tech_design_spec §6.1 (+ D6 / D8). Visual layout follows
 * the Figma "INSi Search" spec: a 2-column field grid on top → full-width
 * "Search INSi" button → full-width "Preview INSi Data" panel below.
 *
 * Form fields (the strict minimum traits — all REQUIRED per the contract §2.1):
 *   - Birth surname      (nomNaissance)
 *   - First name(s)      (single field, split on spaces → prenoms list)
 *   - Date of birth      (dateNaissance, YYYY-MM-DD on the wire)
 *   - Birth sex          (M / F toggle)
 * Plus the OPTIONAL disambiguator:
 *   - Place of birth INSEE (lieuNaissance) — rendered always but DISABLED until
 *     the CR02 "multiple matches" state (EF_INS10_05 / D8).
 *
 * The "Preview INSi Data" panel renders by hook state (idle / loading / cr00 /
 * cr01 / cr02 / error). "Confirm – Use this identity" is enabled only on CR00.
 */
import { Fragment, useMemo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Field, useFormState } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useTranslation } from 'react-i18next';
import { format, formatISO, isValid, parseISO } from 'date-fns';

import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  CircularProgress,
  ToggleButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  SearchOff as SearchOffIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Groups as GroupsIcon,
  InfoOutlined as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

import { Accordion } from 'components';
import { FormTextField, DatePicker } from 'components/ui';
import DialogTitle from 'components/Dialogs/DialogTitle';

import {
  insiTraitsSchema,
  transliterateTrait,
  LIEU_NAISSANCE_REGEX,
} from 'services/insi/traitsValidation';
import { INS_TYPE_LABEL } from 'services/insi/constants';
import { useInsiSearch } from './useInsiSearch';
import LieuNaissanceAutocomplete from './LieuNaissanceAutocomplete';
import { lookupCommuneByCode } from 'services/geo/communes';
import {
  SFields,
  SRow,
  SField,
  SAddNameButton,
  SSexRow,
  SSexToggle,
  SLabel,
  SSearchButton,
  SDivider,
  SPreviewTitle,
  SPreviewPanel,
  SCentered,
  SMessagePanel,
  SIconBox,
  SMessageTitle,
  SMessageDescription,
  SPanelActions,
  SFoundCard,
  SFoundHeader,
  SDetailRow,
  SInsNumber,
  SHistoryGrid,
  SDedup,
  SFooter,
  SCancelButton,
  SConfirmButton,
} from './insiSearchStyled';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Keep only the non-empty prénom items (index 0 = first usual prénom). */
const cleanPrenoms = (list) =>
  (list || []).map((p) => (p || '').trim()).filter((p) => p.length > 0);

/**
 * Normalize a returned `listePrenom` into an array. The contract documents a
 * list, but the backend currently returns a single (space-separated) string —
 * accept both so the CR00 preview and the create-form mapping (T08) get an array.
 */
const normalizePrenoms = (value) => {
  if (Array.isArray(value)) return cleanPrenoms(value);
  if (typeof value === 'string' && value.trim()) {
    return value.trim().split(/\s+/);
  }
  return [];
};

/** Coerce a form date value (Date | ISO string) into 'YYYY-MM-DD' or ''. */
const toDateString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const parsed = parseISO(value);
    return isValid(parsed) ? value.slice(0, 10) : '';
  }
  return isValid(value) ? formatISO(value, { representation: 'date' }) : '';
};

/** Format an ISO 8601 DateTime to dd/MM/yyyy (null when absent/invalid). */
const formatIsoDate = (value) => {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : null;
};

/** Validity period of a historical INS: "start → end" ('…' for an open bound). */
const formatPeriod = (start, end) => {
  const s = formatIsoDate(start);
  const e = formatIsoDate(end);
  if (!s && !e) return '';
  return `${s || '…'} → ${e || '…'}`;
};

/** Format a 15-digit INS matricule readably: "X XX XX XX XXX XXX XX". */
const formatMatricule = (value) => {
  if (!value) return '';
  const digits = value.replace(/\s+/g, '');
  if (digits.length !== 15) return value;
  return [
    digits.slice(0, 1),
    digits.slice(1, 3),
    digits.slice(3, 5),
    digits.slice(5, 7),
    digits.slice(7, 10),
    digits.slice(10, 13),
    digits.slice(13, 15),
  ].join(' ');
};

/**
 * Custom validate adapter: the form holds a `prenoms` array + a Date object, but
 * the shared Yup schema validates the wire shape. Map form values → schema
 * shape, validate SYNCHRONOUSLY, then surface a flat error map keyed by the form
 * field names.
 *
 * NOTE: this MUST be synchronous. An async record-level validate leaves stale
 * errors in react-final-form (e.g. "Required" lingering on a filled field), so
 * we use Yup's `validateSync` rather than the async `validate`.
 */
// Build the Yup schema ONCE (rebuilding it on every keystroke is expensive and
// makes synchronous per-keystroke validation laggy).
// Lenient schema (optional `lieuNaissance` with the INSEE format check) — so the
// 5-char INSEE place of birth is validated when present (the CR02 retry), while
// staying optional on the first call.
let cachedTraitsSchema;
const getTraitsSchema = () => {
  if (!cachedTraitsSchema) cachedTraitsSchema = insiTraitsSchema();
  return cachedTraitsSchema;
};

const validateTraits = (values) => {
  const errors = {};
  // Validate the values that will actually be sent (the hook transliterates
  // traits before calling INSi), so casing/accents never reject the user — the
  // birth surname is shown in CAPS live, prénom items are typed freely.
  const candidate = {
    nomNaissance: transliterateTrait(values.nomNaissance || ''),
    prenoms: cleanPrenoms(values.prenoms).map(transliterateTrait),
    sexe: values.sexe || '',
    dateNaissance: toDateString(values.dateOfBirth),
    lieuNaissance: (values.lieuNaissance || '').toUpperCase(),
  };

  // `prenoms` is an ARRAY field (prenoms[0], prenoms[1], …), so its errors MUST
  // be an array too — returning a string at `errors.prenoms` makes final-form
  // crash ("Cannot set a numeric property on an object") when it places the
  // field-level error at prenoms[0].
  const prenomErrors = [];

  try {
    getTraitsSchema().validateSync(candidate, { abortEarly: false });
  } catch (err) {
    (err.inner || []).forEach((e) => {
      const path = e.path || '';
      if (path === 'nomNaissance') {
        errors.nomNaissance = errors.nomNaissance || e.message;
      } else if (path === 'sexe') {
        errors.sexe = errors.sexe || e.message;
      } else if (path === 'dateNaissance') {
        errors.dateOfBirth = errors.dateOfBirth || e.message;
      } else if (path === 'lieuNaissance') {
        errors.lieuNaissance = errors.lieuNaissance || e.message;
      } else if (path === 'prenoms' || path.startsWith('prenoms')) {
        // 'prenoms' = array-level (min 1) → attach to the first box;
        // 'prenoms[i]' = a specific item.
        const match = path.match(/prenoms\[(\d+)\]/);
        const idx = match ? Number(match[1]) : 0;
        if (!prenomErrors[idx]) prenomErrors[idx] = e.message;
      }
    });
  }

  if (prenomErrors.length) {
    errors.prenoms = prenomErrors;
  }

  return errors;
};

/**
 * Side-effect helper: when the PS edits any of the CORE traits after a result,
 * reset the search back to `idle` — so a different search isn't blocked by the
 * CR02 refine-mode (which gates the button on a valid INSEE). Editing a trait
 * clears the stale result, re-disables the INSEE field, and turns the button
 * back into a first-call "Search INSi". `lieuNaissance` edits do NOT trigger it.
 */
function ResetSearchOnTraitChange({ active, onReset }) {
  const { values } = useFormState({ subscription: { values: true } });
  const signature = JSON.stringify([
    values.nomNaissance || '',
    values.prenoms || [],
    values.sexe || '',
    values.dateOfBirth || null,
  ]);
  const prevSignature = useRef(signature);

  useEffect(() => {
    if (prevSignature.current !== signature) {
      prevSignature.current = signature;
      if (active) onReset();
    }
  }, [signature, active, onReset]);

  return null;
}

ResetSearchOnTraitChange.propTypes = {
  active: PropTypes.bool.isRequired,
  onReset: PropTypes.func.isRequired,
};

const minTraitsValid = (values) =>
  Boolean(
    values.nomNaissance &&
      cleanPrenoms(values.prenoms).length > 0 &&
      values.sexe &&
      toDateString(values.dateOfBirth)
  );

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function InsiSearchModal({
  open,
  onClose,
  initialTraits,
  onConfirm,
  onCreateProvisional,
  onLinkExisting,
}) {
  const { t } = useTranslation();
  const {
    state,
    candidate,
    existingPatient,
    existingPatientId,
    search,
    retryWithLieu,
    retry,
    reset,
  } = useInsiSearch();

  const initialValues = useMemo(
    () => ({
      nomNaissance: initialTraits?.nomNaissance || '',
      // One empty box by default; the PS adds more via "+ Add first name".
      prenoms: initialTraits?.prenoms?.length ? initialTraits.prenoms : [''],
      sexe: initialTraits?.sexe || '',
      dateOfBirth: initialTraits?.dateOfBirth || null,
      lieuNaissance: '',
    }),
    [initialTraits]
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  const isCr02 = state === 'cr02';

  // Focus target for the CR01 "Refine the search" action (the usual cause of a
  // no-match is a trait typo, so we send the PS back to the first trait field).
  const nomNaissanceRef = useRef(null);
  const handleRefine = () => {
    reset();
    nomNaissanceRef.current?.focus();
  };

  const handleSearch = (values) => {
    if (isCr02) {
      retryWithLieu(values.lieuNaissance);
      return;
    }
    search({
      nomNaissance: values.nomNaissance,
      prenoms: cleanPrenoms(values.prenoms),
      sexe: values.sexe,
      dateNaissance: toDateString(values.dateOfBirth),
    });
  };

  const handleConfirm = () => {
    if (!candidate) return;
    // Guard: never create a patient for an INS that already belongs to a local
    // record (D4). The button is disabled in this case, but defend the path too.
    if (existingPatientId) return;
    // Normalize listePrenom (string-or-list → list) before handing the identity
    // to the create form (T08), so its name-mapping gets a proper array.
    onConfirm(
      { value: candidate.ins.value, type: candidate.ins.type },
      { ...candidate.tiq, listePrenom: normalizePrenoms(candidate.tiq?.listePrenom) }
    );
  };

  const searchDisabled = (values) =>
    state === 'loading' ||
    !minTraitsValid(values) ||
    // CR02 retry needs a well-formed 5-char INSEE place of birth.
    (isCr02 && !LIEU_NAISSANCE_REGEX.test((values.lieuNaissance || '').toUpperCase()));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="insi-search-dialog-title"
      PaperProps={{ style: { width: 920, maxWidth: 920, borderRadius: 8 } }}
    >
      <DialogTitle id="insi-search-dialog-title" onClose={handleClose}>
        <Typography variant="h4">{t('INSi Search')}</Typography>
      </DialogTitle>

      <Form
        onSubmit={handleSearch}
        validate={validateTraits}
        initialValues={initialValues}
        mutators={arrayMutators}
        render={({ handleSubmit, form, values }) => (
          <form onSubmit={handleSubmit}>
            <ResetSearchOnTraitChange
              active={state !== 'idle' && state !== 'loading'}
              onReset={() => {
                reset();
                // A changed core trait starts a fresh search, where lieuNaissance
                // must not be sent (EF_INS10_05). Clear it so the stale place of
                // birth doesn't linger in the (now disabled) refine field.
                form.change('lieuNaissance', '');
              }}
            />
            <DialogContent>
              <SFields>
                <SRow>
                  <SField>
                    <SLabel>{t('Birth surname')} *</SLabel>
                    <FormTextField
                      name="nomNaissance"
                      dataId="insi-nom-naissance"
                      placeholder={t('Birth surname')}
                      parse={transliterateTrait}
                      inputRef={nomNaissanceRef}
                    />
                  </SField>
                  <SField>
                    <SLabel>{t('First name(s)')} *</SLabel>
                    {(values.prenoms || ['']).map((_item, idx) => (
                      <FormTextField
                        // eslint-disable-next-line react/no-array-index-key
                        key={idx}
                        name={`prenoms[${idx}]`}
                        dataId="insi-first-name"
                        placeholder={t('First name')}
                        withDeleteButton={idx > 0}
                        onDelete={() => form.mutators.remove('prenoms', idx)}
                      />
                    ))}
                    <SAddNameButton
                      type="button"
                      data-id="insi-add-first-name"
                      onClick={() => form.mutators.push('prenoms', '')}
                    >
                      {'+ ' + t('Add first name')}
                    </SAddNameButton>
                  </SField>
                </SRow>

                <SRow>
                  <SField>
                    <SLabel>{t('Date of birth')} *</SLabel>
                    <DatePicker
                      name="dateOfBirth"
                      dataId="insi-date-of-birth"
                      placeholder={t('dd.mm.yyyy')}
                      openTo="year"
                      disableFuture
                    />
                  </SField>
                  <SField>
                    <SLabel>
                      {t('Place of birth')}
                      {isCr02 ? ' *' : ''}
                    </SLabel>
                    <LieuNaissanceAutocomplete
                      name="lieuNaissance"
                      dataId="insi-lieu-naissance"
                      placeholder={t('Start typing a city, or enter a 5-character INSEE code')}
                      disabled={!isCr02}
                      highlight={isCr02}
                      autoFocus={isCr02}
                      helperText={
                        isCr02
                          ? t(
                              'Several identities match — add the place of birth to find the right one.'
                            )
                          : undefined
                      }
                    />
                  </SField>
                </SRow>

                <SSexRow>
                  <SLabel>{t('Birth sex')} *</SLabel>
                  <Field name="sexe">
                    {({ input }) => (
                      <SSexToggle
                        exclusive
                        value={input.value || null}
                        onChange={(_e, val) => val && input.onChange(val)}
                        data-id="insi-sexe"
                      >
                        <ToggleButton value="M" aria-label={t('Male')}>
                          <MaleIcon />
                        </ToggleButton>
                        <ToggleButton value="F" aria-label={t('Female')}>
                          <FemaleIcon />
                        </ToggleButton>
                      </SSexToggle>
                    )}
                  </Field>
                </SSexRow>

                <SSearchButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disableElevation
                  data-id="insi-search-submit"
                  disabled={searchDisabled(values)}
                  startIcon={<SearchIcon />}
                >
                  {isCr02 ? t('Refine search') : t('Search INSi')}
                </SSearchButton>

                <SDivider />

                <SPreviewTitle>{t('Preview INSi Data')}</SPreviewTitle>
                <SPreviewPanel data-id="insi-preview-panel">
                  <PreviewContent
                    state={state}
                    candidate={candidate}
                    existingPatient={existingPatient}
                    existingPatientId={existingPatientId}
                    onCreateProvisional={onCreateProvisional}
                    onLinkExisting={onLinkExisting}
                    onRefine={handleRefine}
                    onRetry={retry}
                    t={t}
                  />
                </SPreviewPanel>
              </SFields>
            </DialogContent>

            <SFooter>
              <SCancelButton
                onClick={handleClose}
                disableElevation
                data-id="insi-cancel"
              >
                {t('Cancel')}
              </SCancelButton>
              <SConfirmButton
                variant="contained"
                color="primary"
                disableElevation
                data-id="insi-confirm"
                // Block creation when this INS already belongs to a local patient
                // (D4 dedup): the only valid path is to open the existing record.
                disabled={
                  state !== 'cr00' || !candidate || Boolean(existingPatientId)
                }
                onClick={handleConfirm}
                startIcon={<CheckIcon />}
              >
                {t('Confirm - Use this identity')}
              </SConfirmButton>
            </SFooter>
          </form>
        )}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Preview-panel content (one block per state)
// ---------------------------------------------------------------------------

function PreviewContent({
  state,
  candidate,
  existingPatient,
  existingPatientId,
  onCreateProvisional,
  onLinkExisting,
  onRefine,
  onRetry,
  t,
}) {
  if (state === 'loading') {
    return (
      <SCentered>
        <CircularProgress size={28} />
      </SCentered>
    );
  }

  if (state === 'cr00' && candidate) {
    // Defensive: tolerate a backend shape that differs from the contract so a
    // mismatch shows blanks instead of crashing the whole modal (blank screen).
    const tiq = candidate.tiq || {};
    const ins = candidate.ins || {};
    const prenomList = normalizePrenoms(tiq.listePrenom);
    const firstName = prenomList[0] || '';
    const historicalIns = candidate.historicalIns || [];
    return (
      <SFoundCard data-id="insi-cr00">
        <SFoundHeader>
          <CheckCircleIcon fontSize="small" />
          {t('Patient found')}
        </SFoundHeader>
        <SDetail label={t('Birth surname')} value={tiq.nomNaissance} />
        <SDetail
          label={t('First name(s)')}
          value={prenomList.join(' ') || firstName}
        />
        <SDetail label={t('Date of birth')} value={tiq.dateNaissance} />
        <BirthPlacePreviewRow code={tiq.lieuNaissance} t={t} />
        <SDetail label={t('Sex')} value={tiq.sexe} />
        <SInsNumber data-id="insi-ins-number">
          <span className="label">
            {t('INS Number')} ({INS_TYPE_LABEL[ins.type] || ins.type})
          </span>
          <span className="value">{formatMatricule(ins.value)}</span>
        </SInsNumber>

        {historicalIns.length > 0 && (
          <Accordion
            title={t('National INS history')}
            startAdornment={<HistoryIcon fontSize="small" color="action" />}
          >
            <SHistoryGrid data-id="insi-historical-ins">
              {historicalIns.map((h, idx) => (
                <Fragment key={`${h.value}-${idx}`}>
                  <span className="type">
                    {INS_TYPE_LABEL[h.type] || h.type}
                  </span>
                  <span className="value">{formatMatricule(h.value)}</span>
                  <span className="period">
                    {formatPeriod(h.periodStart, h.periodEnd)}
                  </span>
                </Fragment>
              ))}
            </SHistoryGrid>
          </Accordion>
        )}

        {existingPatientId && (
          <SDedup data-id="insi-dedup">
            <Typography variant="body2">
              {existingPatient
                ? t(
                    'This INS already belongs to {{name}} ({{dob}}) in this workspace.',
                    {
                      name: existingPatient.displayName,
                      dob: existingPatient.dateOfBirth,
                    }
                  )
                : t('This INS already belongs to an existing patient.')}
            </Typography>
            <Typography variant="body2">
              {t(
                'You cannot create a duplicate — open the existing record instead.'
              )}
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disableElevation
              data-id="insi-link-existing"
              onClick={() => onLinkExisting(existingPatientId)}
            >
              {t('Open existing patient')}
            </Button>
          </SDedup>
        )}
      </SFoundCard>
    );
  }

  if (state === 'cr01') {
    return (
      <SMessagePanel data-id="insi-cr01">
        <SIconBox>
          <SearchOffIcon />
        </SIconBox>
        <SMessageTitle>{t('No Patient Found in INSi')}</SMessageTitle>
        <SMessageDescription>
          {t(
            'No matching identity was returned. Create this patient as Provisional, or refine the traits and search again.'
          )}
        </SMessageDescription>
        <SPanelActions>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disableElevation
            data-id="insi-create-provisional"
            onClick={onCreateProvisional}
          >
            {t('Create as Provisional')}
          </Button>
          <Button size="small" data-id="insi-refine" onClick={onRefine}>
            {t('Refine the search')}
          </Button>
        </SPanelActions>
      </SMessagePanel>
    );
  }

  if (state === 'cr02') {
    return (
      <SMessagePanel data-id="insi-cr02">
        <SIconBox>
          <GroupsIcon />
        </SIconBox>
        <SMessageTitle>{t('Multiple candidates found')}</SMessageTitle>
        <SMessageDescription>
          {t('Use the place of birth field above to narrow the results.')}
        </SMessageDescription>
      </SMessagePanel>
    );
  }

  if (state === 'error') {
    return (
      <SMessagePanel data-id="insi-error">
        <SIconBox>
          <InfoIcon />
        </SIconBox>
        <SMessageTitle>
          {t('INSi service is temporarily unavailable')}
        </SMessageTitle>
        <SMessageDescription>
          {t(
            'The national identity service could not be reached. This is usually a temporary issue.'
          )}
        </SMessageDescription>
        <SPanelActions>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disableElevation
            data-id="insi-retry"
            onClick={onRetry}
          >
            {t('Search again')}
          </Button>
          <Button
            size="small"
            data-id="insi-error-provisional"
            onClick={onCreateProvisional}
          >
            {t('Create as Provisional')}
          </Button>
        </SPanelActions>
      </SMessagePanel>
    );
  }

  // idle
  return (
    <SMessagePanel data-id="insi-idle">
      <SIconBox>
        <SearchIcon />
      </SIconBox>
      <SMessageTitle>{t('No Data Found')}</SMessageTitle>
      <SMessageDescription>
        {t('Fill the form above and find the INSi data here')}
      </SMessageDescription>
    </SMessagePanel>
  );
}

// ---------------------------------------------------------------------------
// Small detail row
// ---------------------------------------------------------------------------

const SDetail = ({ label, value }) => (
  <SDetailRow>
    <span className="label">{label}</span>
    <span className="value">{value}</span>
  </SDetailRow>
);

SDetail.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

/**
 * Place-of-birth row for the CR00 preview: resolves the INSi `lieuNaissance`
 * code to a readable commune so the PS can verify the place before confirming
 * (display only — it's persisted later by the create/edit auto-fill). Shows
 * "City (code)" once resolved, the bare code while loading or for a
 * foreign/unresolvable code, and "Not provided" if INSi returned no code. The
 * lookup is cached, so it shares its single request with the create/edit screen.
 */
function BirthPlacePreviewRow({ code, t }) {
  const [commune, setCommune] = useState(undefined); // undefined = resolving

  useEffect(() => {
    let cancelled = false;
    if (!code) return undefined;
    setCommune(undefined);
    lookupCommuneByCode(code).then((c) => {
      if (!cancelled) setCommune(c);
    });
    return () => {
      cancelled = true;
    };
  }, [code]);

  let value;
  if (!code) value = t('Not provided');
  else if (commune) value = `${commune.nom} (${code})`;
  else value = code; // resolving, or foreign/unresolvable → show the raw code
  return <SDetail label={t('Place of birth')} value={value} />;
}

BirthPlacePreviewRow.propTypes = {
  code: PropTypes.string,
  t: PropTypes.func.isRequired,
};


InsiSearchModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialTraits: PropTypes.shape({
    nomNaissance: PropTypes.string,
    prenoms: PropTypes.arrayOf(PropTypes.string),
    sexe: PropTypes.oneOf(['M', 'F']),
    dateOfBirth: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
  }),
  onConfirm: PropTypes.func.isRequired,
  onCreateProvisional: PropTypes.func.isRequired,
  onLinkExisting: PropTypes.func.isRequired,
};

PreviewContent.propTypes = {
  state: PropTypes.string.isRequired,
  candidate: PropTypes.object,
  existingPatient: PropTypes.object,
  existingPatientId: PropTypes.string,
  onCreateProvisional: PropTypes.func.isRequired,
  onLinkExisting: PropTypes.func.isRequired,
  onRefine: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};
